<template>
    <div class="grid gap-5">
        <button
            v-for="pricingTier in formattedPricingTiers"
            :key="pricingTier.quantityBreak"
            class="grid grid-cols-3 gap-5 rounded p-5 leading-tight ring-1 ring-inset transition"
            :class="
                modelValue === pricingTier.quantityBreak &&
                !customQuantitySelected
                    ? 'ring-3 ring-grind'
                    : 'ring-grind/20 hover:ring-grind'
            "
            :data-cy="`design-lab-quantity-${pricingTier.quantityBreak}-btn`"
            @click="onSelectQuantity(pricingTier.quantityBreak)"
        >
            <span class="text-left">
                {{ pricingTier.quantityBreakFormatted }}
            </span>

            <span class="font-semibold">
                {{ pricingTier.totalFormatted }}
            </span>

            <span
                v-if="pricingTier.discountPercentage"
                class="text-right text-vibe"
            >
                Save {{ pricingTier.discountPercentage }}%
            </span>
        </button>

        <button
            class="rounded p-5 leading-tight ring-1 ring-inset transition"
            :class="{
                'ring-3 ring-grind': customQuantitySelected,
                'ring-grind/20 hover:ring-grind': !customQuantitySelected,
            }"
            @click="customQuantitySelected = true"
        >
            Enter quantity
        </button>

        <div>
            <div v-if="customQuantitySelected">
                <div class="flex items-center gap-5">
                    <div>
                        <ConfiguratorQuantityInput
                            :model-value="modelValue"
                            :min="minimumQty"
                            :step="quantityStep"
                            @update:model-value="
                                (value: number) => onSelectQuantity(value, true)
                            "
                        />
                    </div>

                    <div>
                        {{ commaNumber(modelValue) }}
                        {{ productTypeLabel }} total
                    </div>
                </div>

                <div v-if="quantityStep > 1" class="mt-3 text-xs text-grind/60">
                    *Sold in increments of {{ quantityStep }}
                    {{ productTypeShortLabelPlural }}
                </div>
            </div>

            <div
                v-if="showTotal && totalFormatted"
                class="fixed bottom-[70px] left-0 flex w-screen flex-row border-t bg-white px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.15)] sm:static sm:w-auto sm:border-t-0 sm:px-0 sm:py-0 sm:shadow-none"
                :class="{ 'sm:mt-3': customQuantitySelected }"
            >
                <div class="flex-grow">
                    <span class="block sm:hidden"> Subtotal </span>
                </div>
                <div
                    class="text-right text-xl font-bold leading-snug sm:text-4xl"
                >
                    {{ totalFormatted }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { FormattedPricingTier, PricingTier } from '~/types/miscellaneous';
import {
    formatPricingTier,
    getPricingTierForQuantity,
} from '~/utils/pricingTiers';

const emit = defineEmits(['update:model-value']);

const props = withDefaults(
    defineProps<{
        modelValue: number;
        showTotal: boolean;
        pricingTiers: PricingTier[];
        maxPricingTiers: number;
        minimumQty: number;
        quantityStep: number;
        productTypeShortLabel: string;
        productTypeShortLabelPlural: string;
    }>(),
    {
        showTotal: true,
        maxPricingTiers: 5,
    },
);

const initialPricingTier = props.pricingTiers.find(
    (pricingTier) => pricingTier.quantityBreak === props.modelValue,
);
const customQuantitySelected = ref(!initialPricingTier);

const formattedPricingTiers = computed<FormattedPricingTier[]>(() => {
    return props.pricingTiers
        .slice(0, props.maxPricingTiers)
        .map((pricingTier) =>
            formatPricingTier(pricingTier, props.pricingTiers[0]),
        );
});

const productTypeLabel = computed(() => {
    return 1 === props.modelValue
        ? props.productTypeShortLabel
        : props.productTypeShortLabelPlural;
});

const totalFormatted = computed(() => {
    const pricingTier = getPricingTierForQuantity(
        props.modelValue,
        props.pricingTiers,
    );

    return pricingTier
        ? formatCurrency(props.modelValue * pricingTier.price)
        : null;
});

const onSelectQuantity = (quantity: number, isCustomQuantity = false) => {
    customQuantitySelected.value = isCustomQuantity;
    emit('update:model-value', quantity, customQuantitySelected.value);
};
</script>
